import { useEffect, useState } from 'react';
import type { Benutzer, MeinProjekt } from '@beholder/shared';
import { MINDMAP_FARBEN, MINDMAP_SYMBOLE, type MindmapKnoten, type MindmapNotiz } from '@beholder/shared/module/mindmap';
import { api } from '../../core/api';
import { Anlagen } from '../../core/Anlagen';
import { Symbol } from '../../core/symbole';
import { Knopf, datumZeit } from '../../core/ui';
import { vorfahren } from './baum';
import { aufgabenSichtbar, PunktAufgaben } from './PunktAufgaben';
import { FARB_NAMEN, FARBTON, SYMBOL_NAMEN, SymbolBild } from './symbole';
import type { KartenZugriff } from './useKarte';
import { WertAnzeige, WertEingabe, type Personen } from './werte';

/** Ein Textfeld mit eigenem Entwurf: Änderungen anderer erscheinen, solange man selbst nicht tippt. */
function useEntwurf(server: string, schluessel: string) {
  const [wert, setWert] = useState(server);
  const [basis, setBasis] = useState(server);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setWert(server);
    setBasis(server);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);
  useEffect(() => {
    if (!dirty) {
      setWert(server);
      setBasis(server);
    }
  }, [server, dirty]);
  return {
    wert,
    basis,
    dirty,
    /** Jemand anderes hat den Wert geändert, während man selbst tippt. */
    fremd: dirty && server !== basis,
    setzen(v: string) {
      setWert(v);
      setDirty(true);
    },
    fertig() {
      setDirty(false);
    },
  };
}

export function KnotenPanel({
  z,
  k,
  projekt,
  darfSchreiben,
  personen,
  ich,
  schliessen,
  waehlen,
  loeschenFragen,
  unterpunkt,
}: {
  z: KartenZugriff;
  k: MindmapKnoten;
  projekt: MeinProjekt;
  darfSchreiben: boolean;
  personen: Personen;
  ich: Benutzer;
  schliessen(): void;
  waehlen(id: string): void;
  loeschenFragen(id: string): void;
  unterpunkt(id: string): void;
}) {
  const titel = useEntwurf(k.titel, k.id);
  const beschreibung = useEntwurf(k.beschreibung, k.id);
  const [notizen, setNotizen] = useState<MindmapNotiz[] | null>(null);
  const [neueNotiz, setNeueNotiz] = useState('');
  const takt = z.notizTakt[k.id] ?? 0;
  const pfadN = `${z.pfad}/knoten/${k.id}/notizen`;

  useEffect(() => {
    let weg = false;
    api
      .get<MindmapNotiz[]>(pfadN)
      .then((n) => !weg && setNotizen(n))
      .catch(() => !weg && setNotizen([]));
    return () => {
      weg = true;
    };
  }, [pfadN, takt]);

  const andere = z.anwesend.filter((p) => p.knotenId === k.id && p.benutzerId !== ich.id);
  const bearbeitenAndere = andere.filter((p) => p.bearbeitet);
  const vor = vorfahren(z.baum, k.id);

  const titelSpeichern = () => {
    const t = titel.wert.trim();
    if (titel.dirty && t && t !== titel.basis) void z.aendern(k.id, { titel: t }, { titel: titel.basis });
    titel.fertig();
  };
  const beschreibungSpeichern = () => {
    if (beschreibung.dirty && beschreibung.wert !== beschreibung.basis) {
      void z.aendern(k.id, { beschreibung: beschreibung.wert }, { beschreibung: beschreibung.basis });
    }
    beschreibung.fertig();
  };

  async function notizSenden() {
    const t = neueNotiz.trim();
    if (!t) return;
    try {
      await api.post(pfadN, { text: t });
      setNeueNotiz('');
      setNotizen(await api.get<MindmapNotiz[]>(pfadN));
    } catch (e) {
      z.setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }

  async function notizLoeschen(n: MindmapNotiz) {
    if (!confirm('Diese Notiz löschen?')) return;
    try {
      await api.delete(`${pfadN}/${n.id}`);
      setNotizen(await api.get<MindmapNotiz[]>(pfadN));
    } catch (e) {
      z.setFehler(e instanceof Error ? e.message : 'Fehler');
    }
  }

  const ton = k.farbe ? FARBTON[k.farbe] : (z.baum.astFarbe.get(k.id) ? FARBTON[z.baum.astFarbe.get(k.id)!] : 'var(--akzent)');

  return (
    <aside className="mm-panel" aria-label={`Punkt ${k.titel}`} onKeyDown={(e) => e.key === 'Escape' && schliessen()}>
      <header className="mm-panel-kopf">
        <nav className="mm-pfad" aria-label="Pfad">
          {vor.map((v) => (
            <button key={v.id} type="button" className="mm-pfad-teil" onClick={() => waehlen(v.id)}>
              {v.titel}
            </button>
          ))}
        </nav>
        <button type="button" className="knopf knopf-leise knopf-klein" aria-label="Seitenleiste schließen" onClick={schliessen}>
          ✕
        </button>
      </header>

      <div className="mm-panel-inhalt">
        <div className="mm-panel-titel" style={{ borderLeftColor: ton }}>
          {darfSchreiben ? (
            <textarea
              className="mm-titel-eingabe"
              value={titel.wert}
              rows={1}
              maxLength={500}
              aria-label="Titel"
              onChange={(e) => titel.setzen(e.target.value.replace(/\n/g, ' '))}
              onBlur={titelSpeichern}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLTextAreaElement).blur();
                }
              }}
            />
          ) : (
            <h2>{k.titel}</h2>
          )}
        </div>
        {titel.fremd && (
          <p className="meldung meldung-info klein">
            {k.geaendertVonName ?? 'Jemand'} hat den Titel inzwischen in „{k.titel}“ geändert.
          </p>
        )}
        {andere.length > 0 && (
          <p className="mm-hinweis-person klein">
            {bearbeitenAndere.length
              ? `${bearbeitenAndere.map((p) => p.name).join(', ')} bearbeitet diesen Punkt gerade.`
              : `${andere.map((p) => p.name).join(', ')} schaut sich diesen Punkt gerade an.`}
          </p>
        )}

        {darfSchreiben && (
          <div className="mm-panel-stil">
            <div className="mm-farben" role="group" aria-label="Farbe">
              <button type="button" className={`mm-farbe mm-farbe-keine${k.farbe ? '' : ' mm-an'}`} title="Keine eigene Farbe" onClick={() => z.aendern(k.id, { farbe: null })}>
                ∅
              </button>
              {MINDMAP_FARBEN.map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`mm-farbe${k.farbe === f ? ' mm-an' : ''}`}
                  style={{ background: FARBTON[f] }}
                  title={FARB_NAMEN[f]}
                  aria-label={FARB_NAMEN[f]}
                  onClick={() => z.aendern(k.id, { farbe: k.farbe === f ? null : f })}
                />
              ))}
            </div>
            <div className="mm-symbole" role="group" aria-label="Symbol">
              {MINDMAP_SYMBOLE.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`mm-symbol-wahl${k.symbol === s ? ' mm-an' : ''}`}
                  title={SYMBOL_NAMEN[s]}
                  aria-label={SYMBOL_NAMEN[s]}
                  onClick={() => z.aendern(k.id, { symbol: k.symbol === s ? null : s })}
                >
                  <SymbolBild symbol={s} groesse={17} />
                </button>
              ))}
            </div>
          </div>
        )}

        <section className="mm-panel-abschnitt">
          <h3>Beschreibung</h3>
          {darfSchreiben ? (
            <textarea
              className="eingabe mm-beschreibung"
              value={beschreibung.wert}
              placeholder="Notizen, Einzelheiten, Links als Text …"
              rows={5}
              onChange={(e) => beschreibung.setzen(e.target.value)}
              onBlur={beschreibungSpeichern}
            />
          ) : k.beschreibung ? (
            <p className="mm-text-block">{k.beschreibung}</p>
          ) : (
            <p className="leise klein">Keine Beschreibung.</p>
          )}
          {beschreibung.fremd && <p className="meldung meldung-info klein">Die Beschreibung wurde inzwischen von {k.geaendertVonName ?? 'jemand anderem'} geändert.</p>}
        </section>

        {z.spalten.length > 0 && (
          <section className="mm-panel-abschnitt">
            <h3>Werte</h3>
            <div className="mm-werte">
              {z.spalten.map((sp) => (
                <label key={sp.id} className="mm-wert-zeile">
                  <span className="leise klein">{sp.name}</span>
                  {darfSchreiben ? (
                    <WertEingabe
                      sp={sp}
                      wert={k.werte[sp.id]}
                      personen={personen}
                      fertig={(neu) => z.aendern(k.id, { werte: { [sp.id]: neu } }, { werte: { [sp.id]: k.werte[sp.id] ?? null } })}
                    />
                  ) : (
                    <span>
                      <WertAnzeige sp={sp} w={k.werte[sp.id]} personen={personen} />
                    </span>
                  )}
                </label>
              ))}
            </div>
          </section>
        )}

        <section className="mm-panel-abschnitt">
          <h3>Anlagen {k.anlagen > 0 && <span className="marke">{k.anlagen}</span>}</h3>
          <Anlagen
            key={z.anlagenTakt[k.id] ?? 0}
            projektId={projekt.id}
            bereich="mindmap"
            bezug={k.id}
            darfSchreiben={darfSchreiben}
            beiAenderung={() => void z.anlagenGeaendert(k.id)}
          />
        </section>

        {aufgabenSichtbar(projekt) && (
          <section className="mm-panel-abschnitt">
            <h3>Aufgaben</h3>
            <PunktAufgaben projekt={projekt} knotenId={k.id} />
          </section>
        )}

        <section className="mm-panel-abschnitt">
          <h3>Notizen {notizen && notizen.length > 0 && <span className="marke">{notizen.length}</span>}</h3>
          {darfSchreiben && (
            <div className="mm-notiz-neu">
              <textarea
                className="eingabe"
                rows={2}
                value={neueNotiz}
                placeholder="Notiz hinzufügen …"
                onChange={(e) => setNeueNotiz(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void notizSenden();
                }}
              />
              <Knopf klein onClick={() => void notizSenden()} disabled={!neueNotiz.trim()}>
                Notiz speichern
              </Knopf>
            </div>
          )}
          {notizen?.length === 0 && <p className="leise klein">Noch keine Notizen.</p>}
          <ul className="mm-notizen">
            {notizen?.map((n) => (
              <li key={n.id}>
                <div className="mm-text-block">{n.text}</div>
                <div className="leise klein reihe">
                  <span>
                    {n.benutzerName ?? (n.quelle === 'Hand' ? 'unbekannt' : 'automatisch')}
                    {n.quelle !== 'Hand' && ` · ${n.quelle}`} · {datumZeit(n.erstelltAm)}
                  </span>
                  {darfSchreiben && (
                    <button type="button" className="knopf knopf-leise knopf-klein" aria-label="Notiz löschen" onClick={() => void notizLoeschen(n)}>
                      {Symbol.muell}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <footer className="mm-panel-fuss">
        {darfSchreiben && (
          <>
            <Knopf klein onClick={() => unterpunkt(k.id)}>
              {Symbol.plus} Unterpunkt
            </Knopf>
            {k.elternId && (
              <Knopf klein art="gefahr" onClick={() => loeschenFragen(k.id)}>
                {Symbol.muell} Löschen
              </Knopf>
            )}
          </>
        )}
        <span className="leise klein mm-geaendert">
          {k.geaendertAm && `Zuletzt ${k.geaendertVonName ? `von ${k.geaendertVonName} ` : ''}geändert: ${datumZeit(k.geaendertAm)}`}
        </span>
      </footer>
    </aside>
  );
}
